import { useQuery } from '@tanstack/react-query';
import { productsApi } from '../services/api.js';
import { 
  cache, 
  CACHE_KEYS, 
  CACHE_TTL, 
  cacheProductStatic, 
  getCachedProductStatic 
} from '../utils/cache.ts';

// Hook for fetching trending products with caching
export const useTrendingProducts = (page = 0, size = 10) => {
  return useQuery({
    queryKey: ['trendingProducts', page, size],
    queryFn: async () => {
      // Check cache first (only for page 0)
      if (page === 0) {
        const cached = cache.get(
          CACHE_KEYS.TRENDING_PRODUCTS, 
          { page, size },
          { ttl: CACHE_TTL.HOME_DATA }
        );
        if (cached) {
          console.log('🟢 Cache HIT: Trending products');
          return cached;
        }
      }

      console.log('🔴 Cache MISS: Fetching trending products from API');
      const data = await productsApi.getTrendingProducts(page, size);
      
      // Cache only page 0
      if (page === 0) {
        cache.set(
          CACHE_KEYS.TRENDING_PRODUCTS,
          data,
          { page, size },
          { ttl: CACHE_TTL.HOME_DATA }
        );
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
};

// Hook for fetching new arrivals with caching
export const useNewArrivals = (page = 0, size = 12, days = 30) => {
  return useQuery({
    queryKey: ['newArrivals', page, size, days],
    queryFn: async () => {
      // Check cache first (only for page 0)
      if (page === 0) {
        const cached = cache.get(
          CACHE_KEYS.NEW_ARRIVALS, 
          { page, size, days },
          { ttl: CACHE_TTL.HOME_DATA }
        );
        if (cached) {
          console.log('🟢 Cache HIT: New arrivals');
          return cached;
        }
      }

      console.log('🔴 Cache MISS: Fetching new arrivals from API');
      const data = await productsApi.getNewArrivals(page, size, days);
      
      // Cache only page 0
      if (page === 0) {
        cache.set(
          CACHE_KEYS.NEW_ARRIVALS,
          data,
          { page, size, days },
          { ttl: CACHE_TTL.HOME_DATA }
        );
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
};

// Hook for fetching home stats with caching
export const useHomeStats = () => {
  return useQuery({
    queryKey: ['homeStats'],
    queryFn: async () => {
      // Check cache first
      const cached = cache.get(
        CACHE_KEYS.HOME_STATS,
        {},
        { ttl: CACHE_TTL.HOME_DATA }
      );
      if (cached) {
        console.log('🟢 Cache HIT: Home stats');
        return cached;
      }

      console.log('🔴 Cache MISS: Fetching home stats from API');
      const data = await productsApi.getHomeStats();
      
      // Cache the data
      cache.set(
        CACHE_KEYS.HOME_STATS,
        data,
        {},
        { ttl: CACHE_TTL.HOME_DATA }
      );
      
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
  });
};

// Hook for fetching products with caching (category pages)
export const useProducts = (params = {}) => {
  return useQuery({
    queryKey: ['products', params],
    queryFn: async () => {
      // Extract category and page for caching strategy
      const { categoryName, page = 0 } = params;
      
      // Check cache for page 0 of category listings
      if (categoryName && page === 0) {
        const cached = cache.get(
          CACHE_KEYS.CATEGORY_PRODUCTS,
          { categoryName, ...params },
          { ttl: CACHE_TTL.CATEGORY_DATA }
        );
        if (cached) {
          console.log(`🟢 Cache HIT: Category products for ${categoryName}`);
          return cached;
        }
      }

      console.log('🔴 Cache MISS: Fetching products from API', params);
      const data = await productsApi.getAllProducts(params);
      
      // Cache page 0 of category listings
      if (categoryName && page === 0) {
        cache.set(
          CACHE_KEYS.CATEGORY_PRODUCTS,
          data,
          { categoryName, ...params },
          { ttl: CACHE_TTL.CATEGORY_DATA }
        );
      }
      
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 3,
    enabled: true,
  });
};

// Hook for fetching single product with partial caching
export const useProduct = (productId) => {
  console.log('%c[useProduct Hook] Called with productId:', 'color: blue; font-weight: bold;', productId);
  console.log('%c[useProduct Hook] productId type:', 'color: blue;', typeof productId);
  console.log('%c[useProduct Hook] Enabled (!!productId):', 'color: blue;', !!productId);
  
  const query = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      console.log('%c[useProduct Hook] Making API call for productId:', 'color: green; font-weight: bold;', productId);
      
      // Get cached static data first
      const cachedStatic = getCachedProductStatic(productId);
      
      // Always fetch fresh data for dynamic fields
      const freshData = await productsApi.getProductById(productId);
      
      if (cachedStatic) {
        console.log('🟢 Cache HIT: Product static data');
        // Merge cached static data with fresh dynamic data
        const mergedData = {
          ...cachedStatic,
          // Override with fresh dynamic data
          variants: freshData.variants, // Contains stock, pricing, discounts
          seller: freshData.seller, // Seller availability
        };
        
        // Update cache with latest static data
        cacheProductStatic(productId, freshData);
        
        return mergedData;
      } else {
        console.log('🔴 Cache MISS: Product static data');
        // Cache static data for next time
        cacheProductStatic(productId, freshData);
        return freshData;
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute (shorter due to dynamic data)
    retry: 3,
    enabled: !!productId, // Only fetch if productId exists
    onSuccess: (data) => {
      console.log('%c[useProduct Hook] API Success:', 'color: green;', data);
    },
    onError: (error) => {
      console.error('%c[useProduct Hook] API Error:', 'color: red;', error);
    },
  });
  
  // Debug query state changes
  console.log('%c[useProduct Hook] Query state:', 'color: blue;', {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    enabled: !!productId,
    data: query.data
  });
  
  return query;
};

// Hook for search products
export const useSearchProducts = (searchTerm, page = 0, size = 20) => {
  return useQuery({
    queryKey: ['searchProducts', searchTerm, page, size],
    queryFn: () => productsApi.getAllProducts({ 
      search: searchTerm, 
      page, 
      size 
    }),
    staleTime: 2 * 60 * 1000,
    enabled: !!searchTerm && searchTerm.length > 2, // Only search if term is longer than 2 chars
  });
};